import React, { Component } from 'react';
import Fade from 'react-reveal/Fade';
import { buildPath } from '../utils/route_utils.js';
import { updateSEO } from '../utils/seo_utils';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import AnnouncementBanner from '../components/AnnouncementBanner';
import Breadcrumb from '../components/Breadcrumb';
import '../css/CareersPage.css';

class CareersPage extends Component {
  constructor(props) {
    super(props);
    this.state = { lang: this.props.lang };
    this.update_lang = this.update_lang.bind(this);
  }

  componentDidMount() {
    window.scrollTo(0, 0);
    updateSEO('career', this.state.lang, '/about/career');
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.lang !== this.state.lang) this.setState({ lang: nextProps.lang });
  }

  update_lang(newLang) { this.props.updateLang(newLang); }

  navigate(route) { window.location.href = buildPath(this.state.lang, route); }

  render() {
    const lang = this.state.lang;

    const hero = {
      title: { zh: "加入 Nexuni", en: "Join Nexuni", ja: "Nexuni に参加する" },
      sub: {
        zh: "與頂尖人才一起打造真正能落地的 AI 機器人。",
        en: "Build AI robots that actually work — with a world-class team.",
        ja: "実際に機能する AI ロボットを、世界レベルのチームと共に。"
      }
    };

    const jobs = [
      {
        id: "SE-robot",
        title: { zh: "機器人軟體工程師", en: "Robot Software Engineer", ja: "ロボットソフトウェアエンジニア" },
        location: { zh: "新竹，台灣", en: "Hsinchu, Taiwan", ja: "台湾、新竹" },
        type: { zh: "全職", en: "Full-time", ja: "フルタイム" },
        summary: {
          zh: "開發機器人軟體系統，包含 ROS2 架構、SLAM 定位、感測器融合、路徑規劃與即時控制。",
          en: "Develop robotic software systems including ROS2 architecture, SLAM localization, sensor fusion, path planning, and real-time control.",
          ja: "ROS2 アーキテクチャ、SLAM 位置推定、センサーフュージョン、経路計画、リアルタイム制御を含むロボットソフトウェアシステムを開発。"
        },
        tags: ["C++", "Python", "ROS2", "SLAM", "NVIDIA Jetson"]
      },
      {
        id: "SE",
        title: { zh: "AI 軟體工程師", en: "AI Software Engineer", ja: "AI ソフトウェアエンジニア" },
        location: { zh: "新竹，台灣", en: "Hsinchu, Taiwan", ja: "台湾、新竹" },
        type: { zh: "全職", en: "Full-time", ja: "フルタイム" },
        summary: {
          zh: "負責 AI 相關專案研發，整合機器學習與邊緣運算平台，優化 AI 辨識效能。",
          en: "Lead AI R&D projects, integrate machine learning with edge computing platforms, and optimize AI recognition performance.",
          ja: "AI 関連プロジェクトの研究開発を担当し、機械学習をエッジコンピューティングに統合し、AI 認識性能を最適化。"
        },
        tags: ["Python", "C++", "PyTorch", "NVIDIA Jetson"]
      },
      {
        id: "SD",
        title: { zh: "事業發展專員", en: "Sales / Business Development", ja: "営業 / 事業開発" },
        location: { zh: "新竹，台灣", en: "Hsinchu, Taiwan", ja: "台湾、新竹" },
        type: { zh: "全職", en: "Full-time", ja: "フルタイム" },
        summary: {
          zh: "發掘商業機會，維護客戶關係，推廣 AI 自動化產品。",
          en: "Identify business opportunities, maintain client relationships, and promote AI automation products.",
          ja: "ビジネスチャンスの発見、顧客関係の維持、AI 自動化製品のプロモーション。"
        },
        tags: []
      },
      {
        id: "HW-robot",
        title: { zh: "機器人硬體設計工程師", en: "Robot Hardware Design Engineer", ja: "ロボットハードウェア設計エンジニア" },
        location: { zh: "新竹，台灣", en: "Hsinchu, Taiwan", ja: "台湾、新竹" },
        type: { zh: "全職", en: "Full-time", ja: "フルタイム" },
        summary: {
          zh: "設計與開發 AI 機器人的機械結構、外殼與硬體整合。",
          en: "Design and develop mechanical structures, enclosures, and hardware integration for AI robots.",
          ja: "AI ロボットの機械構造、筐体、ハードウェア統合の設計・開発。"
        },
        tags: ["SolidWorks", "AutoCAD", "CNC", "3D Printing"]
      }
    ];

    const internships = [
      {
        id: "softwareIntern",
        title: { zh: "軟體開發實習", en: "Software Engineering Intern", ja: "ソフトウェアエンジニアリングインターン" },
        img: "/image/software_intern_intro.png",
        summary: {
          zh: "參與 AI 機器人、智慧商店、停車場系統的開發，使用 Python/C++ 進行專案實作。",
          en: "Work on AI robotics, smart store, and parking systems using Python/C++ in real project settings.",
          ja: "Python/C++ を使用した AI ロボティクス、スマートストア、駐車システムの開発に参加。"
        }
      },
      {
        id: "hardwareIntern",
        title: { zh: "硬體設計開發實習", en: "Hardware Engineering Intern", ja: "ハードウェアエンジニアリングインターン" },
        img: "/image/hardware_intern_intro.png",
        summary: {
          zh: "參與機器人平台、IoT 模組的機構設計，學習實務機構設計技能。",
          en: "Design robot platforms and IoT modules, gaining hands-on mechanical design experience.",
          ja: "ロボットプラットフォームと IoT モジュールの設計に参加し、実践的な機構設計スキルを習得。"
        }
      }
    ];

    const culture = [
      {
        img: "/image/life_main.jpg",
        title: { zh: "創新", en: "Innovation", ja: "創新" },
        desc: {
          zh: "透過持續實驗探索機器人與 AI 的極限。在這裡，創新不只是理念，更是日常工作的一部分。",
          en: "We explore the limits of robotics through constant experimentation. Innovation isn't a concept here — it's daily work.",
          ja: "ロボットと AI の可能性を日々の実験で切り拓く。革新は概念ではなく、日常業務そのものです。"
        }
      },
      {
        img: "/image/safety_ethics.jpg",
        title: { zh: "實務經驗學習", en: "Hands-on Learning", ja: "実務経験からの学習" },
        desc: {
          zh: "每位員工都有機會直接參與前線工作，從真實挑戰中學習成長。",
          en: "Every team member works on the frontline, learning from real-world challenges.",
          ja: "全員がフロントラインに立ち、実際の課題から学ぶ。"
        }
      },
      {
        img: "/image/environment_1.jpg",
        title: { zh: "共同成長", en: "Growth Together", ja: "共に成長する" },
        desc: {
          zh: "根據個人專業與職涯發展靈活調整職務，鼓勵跨領域整合與創新。",
          en: "We adjust roles to match individual strengths and career goals. Cross-disciplinary growth defines our culture.",
          ja: "自由と柔軟性を重視し、個々のキャリアに合わせた職務調整で共に成長します。"
        }
      },
      {
        img: "/image/life_last.jpg",
        title: { zh: "Nexuni 大家庭", en: "Nexuni Family", ja: "Nexuni ファミリー" },
        desc: {
          zh: "Nexuni 連結所有人。相同的遠見與企圖心，打造一個舒適且激勵人心的環境。",
          en: "Nexuni brings everybody together. Same vision, same ambition — a community where people feel empowered.",
          ja: "同じビジョンを持つ仲間が集まり、快適で力を与える環境を共に創ります。"
        }
      }
    ];

    const testimonials = [
      {
        name: "Brian Wang",
        img: "/image/intern_wang.jpg",
        school: { zh: "臺大電資所碩士", en: "M.S. NTU EECS", ja: "台湾大学電気情報修士" },
        route: "/about/internSE2022Spring",
        quote: {
          zh: "工作內容幾乎是從零開始的專案，有別於其他公司的實習偏打雜跟維護，這是在這裡實習最大的收穫。",
          en: "I was assigned projects built from scratch — unlike many other internships focused on maintenance, this was the biggest takeaway.",
          ja: "ゼロからのプロジェクトに取り組めたことが、他のインターンシップとは大きく異なる最大の収穫でした。"
        }
      },
      {
        name: "Xianglin Wu",
        img: "/image/intern_wu.jpg",
        school: { zh: "政大國貿雙電物學程", en: "NCCU Int'l Business & EE/Physics", ja: "政治大学国際貿易・電気物理" },
        route: "/about/internSE2022Spring_wu",
        quote: {
          zh: "在 Nexuni 實習讓我接觸到真實的產品開發流程，從需求分析到系統部署都有參與。",
          en: "Interning at Nexuni exposed me to real product development — from requirement analysis to system deployment.",
          ja: "Nexuni でのインターンで、要件分析からシステムデプロイまで、実際の製品開発プロセスを経験しました。"
        }
      },
      {
        name: "YuHsuan Hsu",
        img: "/image/intern_hsu.jpg",
        school: { zh: "台大機械系", en: "NTU Mechanical Eng.", ja: "台湾大学機械工学" },
        route: "/about/internME2022Spring",
        quote: {
          zh: "在 Nexuni 不僅學到硬體設計與實作，也能參與會議交流分享想法，每次會議都很有效率。",
          en: "At Nexuni I learned not just hardware design but also how to share ideas in efficient team meetings.",
          ja: "Nexuni ではハードウェア設計だけでなく、効率的なチームミーティングでのアイデア共有も学びました。"
        }
      }
    ];

    const lifeGallery = [
      { src: "/image/life/team_trip_japan_snow.jpg", alt: { zh: "日本員工旅遊", en: "Team Trip — Japan", ja: "社員旅行 — 日本" } },
      { src: "/image/life/robot_training.jpeg", alt: { zh: "機器人遙操作訓練", en: "Robot Teleoperation Training", ja: "ロボット遠隔操作訓練" } },
      { src: "/image/life/team_outing_2025.jpg", alt: { zh: "2025 員工旅遊", en: "2025 Team Outing", ja: "2025 社員旅行" } },
      { src: "/image/life/monthly_meeting.jpeg", alt: { zh: "月會", en: "Monthly Meeting", ja: "月例会議" } },
      { src: "/image/life/team_trip_japan_castle.jpg", alt: { zh: "日本員工旅遊", en: "Team Trip — Japan", ja: "社員旅行 — 日本" } },
      { src: "/image/life/team_trip_korea.jpeg", alt: { zh: "韓國員工旅遊", en: "Team Trip — Korea", ja: "社員旅行 — 韓国" } }
    ];

    const lifeValues = {
      zh: "Nexuni 不只打造 AI 機器人 — 我們打造一個讓人才成長的環境。從持續實驗中追求創新，從第一線的挑戰中學習，根據個人專長靈活調整職務，鼓勵跨領域合作。透過定期月會、技術分享、員工旅遊與團隊活動，每一位 Nexuni 成員都是大家庭的一份子。",
      en: "Nexuni doesn't just build AI robots — we build an environment where people thrive. We push the boundaries of innovation through constant experimentation, learn from real-world challenges on the frontline, and adjust roles to match individual strengths. Through monthly meetings, tech talks, team trips, and group activities, every Nexuni member is part of the family.",
      ja: "Nexuni は AI ロボットだけでなく、人が成長できる環境を築いています。日々の実験で革新を追求し、現場の課題から学び、個々の強みに合わせて柔軟に役割を調整します。月例会議、技術共有、社員旅行、チーム活動を通じて、すべてのメンバーがファミリーの一員です。"
    };

    const sectionLabels = {
      positions: { zh: "開放職缺", en: "Open Positions", ja: "募集職種" },
      internship: { zh: "實習計畫", en: "Internship Program", ja: "インターンシップ" },
      life: { zh: "文化與環境", en: "Life at Nexuni", ja: "企業文化" },
      voices: { zh: "實習生心得", en: "Intern Voices", ja: "インターン体験談" },
      viewDetails: { zh: "查看詳情", en: "View Details", ja: "詳細を見る" },
      apply: { zh: "投遞履歷", en: "Apply Now", ja: "応募する" },
      applyEmail: { zh: "請寄履歷至 hr@nexuni.com", en: "Send your resume to hr@nexuni.com", ja: "履歴書を hr@nexuni.com に送信してください" },
      or104: { zh: "或至 104 人力銀行查看所有職缺", en: "Or view all positions on 104 Job Bank", ja: "または 104 Job Bank で全ての求人を見る" },
      internNote: {
        zh: "定期於每年一月和七月開始進行為期半年的實習生計畫，亦有提供暑期實習。實習計畫時程相較彈性，可聯絡 HR 以提供最近的 onboard 時程。若想收到實習資訊通知，歡迎來信訂閱電子報 contact@nexuni.com 或透過 Instagram, Facebook 與我們聯絡。",
        en: "We run a six-month internship program starting every January and July, with summer internships also available. The schedule is flexible — please contact HR for the latest onboarding timeline. To receive updates, email contact@nexuni.com or follow us on Instagram and Facebook.",
        ja: "毎年1月と7月に開始する半年間のインターンシッププログラムを実施しており、夏季インターンシップも提供しています。スケジュールは柔軟に対応可能ですので、最新のオンボーディング時期については HR までお問い合わせください。最新情報を受け取りたい方は contact@nexuni.com にメールいただくか、Instagram・Facebook をフォローしてください。"
      }
    };

    const crumb = { zh: "職涯", en: "Careers", ja: "キャリア" };
    // Cards are clickable divs: make them reachable and operable by keyboard.
    const keyNav = (go) => (e) => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); go(); }
    };

    return (
      <div id="body" className={lang + " careers_page"}>
        <AnnouncementBanner lang={lang} />
        <Navbar lang={lang} updateLangFunc={this.update_lang} />
        <Breadcrumb lang={lang} items={[{ label: crumb }]} />

        {/* Hero: photo under an ink scrim, copy lower-left */}
        <section className="car_hero">
          <div className="car_hero_media" aria-hidden="true">
            <img src={process.env.PUBLIC_URL + '/image/career_main.jpg'} alt="" loading="eager" />
          </div>
          <div className="car_hero_scrim" aria-hidden="true" />
          <div className="car_hero_content">
            <h1>{hero.title[lang]}</h1>
            <p>{hero.sub[lang]}</p>
          </div>
        </section>

        {/* ===== Open Positions ===== */}
        <section className="car_section car_section_base">
          <div className="car_section_inner">
            <h2 className="car_section_title">{sectionLabels.positions[lang]}</h2>
            <div className="car_jobs_grid">
              {jobs.map((job, i) => (
                <Fade key={i} duration={300} delay={i * 50}>
                  <div className="car_job_card" role="link" tabIndex={0}
                    onClick={() => this.navigate('/about/' + job.id)}
                    onKeyDown={keyNav(() => this.navigate('/about/' + job.id))}>
                    <div className="car_job_card_head">
                      <h3>{job.title[lang]}</h3>
                      <div className="car_job_meta">
                        <span className="car_job_location">{job.location[lang]}</span>
                        <span className="car_job_type">{job.type[lang]}</span>
                      </div>
                    </div>
                    <p className="car_job_summary">{job.summary[lang]}</p>
                    {job.tags.length > 0 && (
                      <div className="car_job_tags">
                        {job.tags.map((t, ti) => <span key={ti} className="car_job_tag">{t}</span>)}
                      </div>
                    )}
                    <span className="car_job_link">{sectionLabels.viewDetails[lang]} &rarr;</span>
                  </div>
                </Fade>
              ))}
            </div>
            <div className="car_apply_bar">
              <p>{sectionLabels.applyEmail[lang]}</p>
              <a href="https://www.104.com.tw/company/1a2x6bm4zo" target="_blank" rel="noopener noreferrer" className="car_104_link">
                {sectionLabels.or104[lang]} &rarr;
              </a>
            </div>
          </div>
        </section>

        {/* ===== Internship ===== */}
        <section className="car_section car_section_alt">
          <div className="car_section_inner">
            <h2 className="car_section_title">{sectionLabels.internship[lang]}</h2>
            <p className="car_intern_note">{sectionLabels.internNote[lang]}</p>
            <div className="car_intern_grid">
              {internships.map((intern, i) => (
                <Fade key={i} duration={300} delay={i * 50}>
                  <div className="car_intern_card" role="link" tabIndex={0}
                    onClick={() => this.navigate('/about/' + intern.id)}
                    onKeyDown={keyNav(() => this.navigate('/about/' + intern.id))}>
                    <div className="car_intern_card_img">
                      <img src={process.env.PUBLIC_URL + intern.img} alt={intern.title[lang]} />
                    </div>
                    <div className="car_intern_card_body">
                      <h3>{intern.title[lang]}</h3>
                      <p>{intern.summary[lang]}</p>
                      <span className="car_intern_link">{sectionLabels.viewDetails[lang]} &rarr;</span>
                    </div>
                  </div>
                </Fade>
              ))}
            </div>

            <h3 className="car_voices_subtitle">{sectionLabels.voices[lang]}</h3>
            <div className="car_testimonials_dark">
              {testimonials.map((t, i) => (
                <Fade key={i} duration={300} delay={i * 50}>
                  <div className="car_testimonial_dark" role="link" tabIndex={0}
                    onClick={() => this.navigate(t.route)}
                    onKeyDown={keyNav(() => this.navigate(t.route))}>
                    <img className="car_testimonial_photo" src={process.env.PUBLIC_URL + t.img} alt={t.name} />
                    <div className="car_testimonial_body">
                      <p>"{t.quote[lang]}"</p>
                      <footer>
                        <strong>{t.name}</strong>
                        <span>{t.school[lang]}</span>
                      </footer>
                      <span className="car_intern_link">{sectionLabels.viewDetails[lang]} &rarr;</span>
                    </div>
                  </div>
                </Fade>
              ))}
            </div>
          </div>
        </section>

        {/* ===== Life at Nexuni ===== */}
        <section className="car_section car_section_base">
          <div className="car_section_inner">
            <h2 className="car_section_title">{sectionLabels.life[lang]}</h2>
            <p className="car_life_intro">{lifeValues[lang]}</p>
            <div className="car_life_gallery">
              {lifeGallery.map((img, i) => (
                <Fade key={i} duration={300} delay={i * 40}>
                  <div className="car_life_gallery_item">
                    <img src={process.env.PUBLIC_URL + img.src} alt={img.alt[lang]} loading="lazy" />
                  </div>
                </Fade>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="car_cta">
          <button className="btn-primary car_cta_btn" onClick={() => window.open('https://www.104.com.tw/company/1a2x6bm4zo', '_blank', 'noopener,noreferrer')}>
            {sectionLabels.apply[lang]} <span className="arrow" aria-hidden="true">&rarr;</span>
          </button>
        </section>

        <Footer lang={lang} navigate={(route) => this.navigate(route)} />
      </div>
    );
  }
}

export default CareersPage;
